import PropTypes from 'prop-types';
import cn from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router';
import { useTimeoutFn } from 'react-use';

import { __t, ServicesGridBlockKind } from '@mssgme/shared';
import { LandingsPropTypes, Spinner } from '@mssgme/ui';
import { useBlockTheme } from '../../../hooks';
import { MoreButton } from '../../../UI';
import { BlockBase } from '../BlockBase';
import { ServicesGridItem } from './ServicesGridItem';
import { ServicesModal } from './ServicesModal';

import styles from './ServicesGrid.scss';

const ITEMS_PER_PAGE = 6;

// TODO: Migrate/convert all service grid items to normal products and remove this block. Replace with Products block.

export default function ServicesGrid({ block, sortable, interactive, delay = 0, ...rest }) {
    const location = useLocation();
    const history = useHistory();
    const hashId = location.hash && location.hash.slice(1);
    const slideIndexFromHash = hashId && block.items.findIndex((item) => item._id === hashId);
    const hasSlideFromHash = Number.isInteger(slideIndexFromHash) && slideIndexFromHash >= 0;
    const { theme } = useBlockTheme(block);
    const [page, setPage] = useState(1);
    const [isDelayed, setDelayed] = useState(!!delay);
    const [isServicesModal, setIsServicesModal] = useState(false);
    const [currentServiceIndex, setCurrentServiceIndex] = useState(0);
    const total = block.items.length;
    const pages = Math.ceil(total / ITEMS_PER_PAGE);
    const hasMore = page < pages;
    const sliced = hasMore ? block.items.slice(0, page * ITEMS_PER_PAGE) : block.items;

    const handleLoadMore = useCallback(() => setPage((old) => old + 1), []);
    const openServicesModal = useCallback(() => {
        setIsServicesModal(true);
    }, []);
    const closeServicesModal = useCallback(() => {
        setIsServicesModal(false);
        history.push(location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        if (hasSlideFromHash && !sortable) {
            setCurrentServiceIndex(slideIndexFromHash);
            openServicesModal();
        }
    }, [hasSlideFromHash]);

    useTimeoutFn(() => setDelayed(false), delay);

    if (isDelayed) {
        return (
            <BlockBase {...rest}>
                <Spinner />
            </BlockBase>
        );
    }

    return (
        <BlockBase lazy {...rest}>
            {sliced.length ? (
                <ul className={cn(styles.servicesList, styles[theme.gridMode])}>
                    {sliced.map((item, index) => (
                        <li key={item._id || index}>
                            <ServicesGridItem
                                item={item}
                                themeStyles={theme}
                                onClick={() => {
                                    setCurrentServiceIndex(index);
                                    openServicesModal();
                                }}
                            />
                        </li>
                    ))}
                </ul>
            ) : (
                <ul
                    className={cn(styles.servicesList, styles[theme.gridMode])}
                    style={{ opacity: 0.7, pointerEvents: 'none' }}
                >
                    <li>
                        <ServicesGridItem
                            themeStyles={theme}
                            item={{
                                title: __t('title'),
                                price: { amount: 9.99, formatted: '$19.99', formattedDiscount: '$9.99' },
                            }}
                        />
                    </li>
                    <li>
                        <ServicesGridItem
                            themeStyles={theme}
                            item={{
                                title: __t('title'),
                                price: { amount: 9.99, formatted: '$19.99', formattedDiscount: '$9.99' },
                            }}
                        />
                    </li>
                </ul>
            )}

            {hasMore && (
                <div className={styles.moreButtonContainer}>
                    <MoreButton
                        onClick={handleLoadMore}
                        style={{
                            backgroundColor: theme.backgroundColor,
                            color: theme.color,
                            borderRadius: theme.borderRadius,
                        }}
                    />
                </div>
            )}
            {isServicesModal && (
                <ServicesModal
                    handleHash={interactive}
                    themeStyles={theme}
                    contacts={block.contacts}
                    services={block.items}
                    activeIndex={currentServiceIndex}
                    interactive={interactive}
                    onClose={closeServicesModal}
                />
            )}
        </BlockBase>
    );
}

ServicesGrid.propTypes = {
    ...BlockBase.propTypes,
    block: LandingsPropTypes.block.isRequired,
    delay: PropTypes.number,
};

ServicesGrid.defaultProps = BlockBase.defaultProps;

ServicesGrid.kind = ServicesGridBlockKind;
